import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PeoplePickerService {
  constructor(private http: HttpClient) {}
  
  // User-wise image cache - cache the Promise to prevent duplicate requests
  private imageCache = new Map<string, Promise<string | null>>();

  searchPeople(term: string): Observable<any[]> {
    return this.http.post<any>(`${environment.apiBaseUrl}/People/Search`, { criteria: term })
      .pipe(map(response => response.value || []));
  }

  // For dashboard header - cached (current logged-in user)
  async getPhoto(email: string): Promise<string | null> {
    if (this.imageCache.has(email)) {
      return this.imageCache.get(email)!;
    }
    
    const photoPromise = this._fetchPhotoCurrentUser(email);
    this.imageCache.set(email, photoPromise);
    
    return photoPromise;
  }

  // For people picker - uncached (different users in search results)
  async getPhotoUncached(email: string): Promise<string | null> {
    return this._fetchPhotoPeoplePicker(email);
  }

  // GET /People/GetImage - for current user (cached by backend via ETag)
  private async _fetchPhotoCurrentUser(email: string): Promise<string | null> {
    try {
      const response = await this.http.get(`${environment.apiBaseUrl}/People/GetImage`, { responseType: 'text' }).toPromise();
      return response && response.length > 500 ? `data:image/jpg;base64,${response}` : null;
    } catch {
      return null;
    }
  }

  // POST /People/GetImage - for people picker (uncached, passes email as body parameter)
  private async _fetchPhotoPeoplePicker(email: string): Promise<string | null> {
    try {
      // Use POST endpoint with email in body to bypass HTTP GET caching
      const response = await this.http.post(`${environment.apiBaseUrl}/People/GetImage`, { Value: email }, { responseType: 'text' }).toPromise();
      return response && response.length > 500 ? `data:image/jpg;base64,${response}` : null;
    } catch {
      return null;
    }
  }
  
  // Invalidate cached image for a specific user (call after profile update)
  invalidateUserImageCache(email: string): void {
    this.imageCache.delete(email);
  }
  
  // Clear all cached images (call on logout)
  clearImageCache(): void {
    this.imageCache.clear();
  }
}